import React, {useState} from "react";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {Grid, Typography, Card, CardHeader, CardContent} from "@mui/material";
import Loading from "../../components/common/Loading";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import {GET_TODAY_APPOINTMENT_OCCUPATION, GET_UPCOMING_APPOINTMENTS,} from "../../gql/dashboard.gql";
import DashboardUpcomingAppointmentsCard from "./DashboardUpcomingAppointmentsCard";

const AppointmentDashboard = () => {
    const {data: occupationData} = useQuery(GET_TODAY_APPOINTMENT_OCCUPATION);
    const {t} = useTranslation();
    const [limit] = useState(10);

    const {data, loading, error} = useQuery(GET_UPCOMING_APPOINTMENTS, {
        variables: {limit},
        pollInterval: 60000,
        fetchPolicy: 'cache-and-network',
    });

    const upcomingAppointmentList = data?.upcomingAppointments || [];

    return (
        <Grid
            container
            spacing={2}
            alignItems="stretch"
            sx={{
                pl: 3,
                pr: 3,
                pt: 2,
            }}
        >
            {/* LEFT COLUMN: KPIs stacked */}
            <Grid item xs={12} sm={4} sx={{display: "flex"}}>
                <Grid container direction="column" spacing={2} sx={{flex: 1}}>

                    <Grid item sx={{flex: 1, display: "flex"}}>
                        <Card variant="outlined" sx={{p: 2, width: "100%", height: "100%"}}>
                            <CardHeader
                                title={
                                    <Typography color="secondary" variant="h5" align="center">
                                        {t("numberOfAppointmentsToday")}
                                    </Typography>
                                }
                            />
                            <CardContent sx={{textAlign: "center"}}>
                                <Typography color="primary" variant="h1">
                                    {occupationData?.appointmentOccupations ?? 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item sx={{flex: 1, display: "flex"}}>
                        <Card variant="outlined" sx={{p: 2, width: "100%", height: "100%"}}>
                            <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('specialAttention')}</Typography>}/>
                            <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                                <CardContent>
                                    {(() => {
                                        const seenAttentionIds = new Set();
                                        const attentionItems = upcomingAppointmentList
                                            ?.flatMap(appointment => appointment?.animal || [])
                                            .flatMap(animal => (animal.attentions || [])
                                                .map(attention => ({animal, attention})))
                                            .filter(({attention}) => {
                                                const id = attention?.id;
                                                if (!id) return true; // keep if no id present
                                                if (seenAttentionIds.has(id)) return false;
                                                seenAttentionIds.add(id);
                                                return true;
                                            }) || [];

                                        if (attentionItems.length === 0) {
                                            return (
                                                <Typography color="primary" component="div">
                                                    {t('noSpecialAttention')}
                                                </Typography>
                                            );
                                        }

                                        return attentionItems.map(({animal, attention}, index) => (
                                            <Typography key={`${attention?.id ?? animal.id}-${index}`} color="primary" variant="p" component="div">
                                                {animal.name} - {attention.subject}
                                            </Typography>
                                        ));
                                    })()}
                                </CardContent>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* RIGHT COLUMN: upcoming appointments */}
            <Grid item xs={12} sm={8} sx={{display: "flex"}}>
                <Card
                    variant="outlined"
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <DashboardUpcomingAppointmentsCard items={upcomingAppointmentList} loading={loading} error={error} limit={10}/>
                </Card>
            </Grid>
        </Grid>);
};

export default withAuthenticationRequired(AppointmentDashboard, {
    onRedirecting: () => <Loading/>, returnTo: '/beheer/login'
});
